import React, { useEffect, useContext, useState, useMemo } from 'react';
import logo from '../assets/images/logo/COLLEGELOGO.jpeg';
import { ProductContext } from '../ProductProvider';
import { coursesOffered, courseDetailsList } from '../ProData';
import Preloader from '../components/Preloader/Preloader';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export default function LandingPage() {
  const { validateLogin, loggedUser, myProfileImage } = useContext(ProductContext);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!loggedUser.isAuthenticated) {
      validateLogin();
    }
    setLoaded(true);
  }, [loggedUser]);

  const getTimeGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const currentDateFormatted = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const getAcademicSession = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    return today.getMonth() >= 5
      ? `${currentYear}-${currentYear + 1}`
      : `${currentYear - 1}-${currentYear}`;
  };

  const userNickname = (loggedUser?.nickname || '').toUpperCase();
  const matchedCourse = coursesOffered.find(
    (course) => course.courseCode && userNickname.includes(course.courseCode.toUpperCase())
  );
  const enrolledCourseName = matchedCourse ? matchedCourse.courseName : 'Bachelor of Theology';
  const enrolledCourseBadge = matchedCourse
    ? (matchedCourse.courseCode === 'BTH'
        ? 'B.Th Degree'
        : matchedCourse.courseCode === 'RTD'
        ? 'RTD Diploma'
        : matchedCourse.courseCode === 'OYT'
        ? '1-Year Course'
        : matchedCourse.courseCode === 'LTH'
        ? 'M.Th Degree'
        : matchedCourse.courseCode === 'DTH'
        ? 'D.Th Degree'
        : `${matchedCourse.courseCode} Degree`)
    : 'B.Th Degree';

  const totalCredits = useMemo(() => {
    return (courseDetailsList || []).reduce((sum, item) => sum + (parseInt(item.courseCredit, 10) || 0), 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Student Portal Dashboard - Vidyajyoti College of Theology Delhi</title>
        <meta name="description" content="Welcome to Vidyajyoti College of Theology Student Portal Dashboard" />
        <link rel="canonical" href="/" />
      </Helmet>

      <Preloader show={!loaded} />

      <div className="dashboard-container animate-fade-in-up">
        {/* Welcome Hero Banner */}
        <div 
          className="dashboard-hero card mb-4 p-4 p-md-5"
          style={{
            background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #2563eb 100%)',
            color: '#ffffff',
            border: 'none',
            borderRadius: '24px',
            boxShadow: '0 20px 35px -10px rgba(30, 58, 138, 0.35)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Decorative Ambient Background Elements */}
          <div 
            style={{
              position: 'absolute',
              top: '-40px',
              right: '-40px',
              width: '240px',
              height: '240px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(245, 158, 11, 0.25) 0%, rgba(245, 158, 11, 0) 70%)',
              pointerEvents: 'none'
            }}
          />
          <div 
            style={{
              position: 'absolute',
              bottom: '-60px',
              left: '30%',
              width: '280px',
              height: '280px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, rgba(59, 130, 246, 0) 70%)',
              pointerEvents: 'none'
            }}
          />

          <div className="row align-items-center position-relative" style={{ zIndex: 1 }}>
            <div className="col-12 col-md-8 mb-4 mb-md-0">
              <div className="d-flex align-items-center gap-2 mb-2">
                <span className="badge" style={{ background: 'rgba(245, 158, 11, 0.25)', color: '#fef3c7', border: '1px solid rgba(245, 158, 11, 0.4)' }}>
                  <i className="fa fa-calendar-check-o me-1"></i> {currentDateFormatted}
                </span>
                <span className="badge" style={{ background: 'rgba(255, 255, 255, 0.15)', color: '#ffffff' }}>
                  Academic Session {getAcademicSession()}
                </span>
              </div>

              <h1 className="display-6 fw-bold mb-2 text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {getTimeGreeting()}, {loggedUser.name || 'Student'}!
              </h1>
              
              <p className="lead text-white-50 mb-3" style={{ fontSize: '1.05rem', maxWidth: '600px' }}>
                Welcome to your <strong className="text-white">Vidyajyoti College of Theology</strong> student portal. Access your curriculum, grades, official marksheet, and personal profile seamlessly.
              </p>

              <div className="d-flex flex-wrap gap-2 pt-1">
                <Link to="/course" className="btn btn-light fw-bold px-4 py-2" style={{ borderRadius: '12px', color: '#1e3a8a', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                  <i className="fa fa-book me-2"></i> View Courses
                </Link>
                <Link to="/marksheet" className="btn fw-bold px-4 py-2" style={{ borderRadius: '12px', background: 'rgba(255, 255, 255, 0.15)', color: '#ffffff', border: '1px solid rgba(255, 255, 255, 0.3)' }}>
                  <i className="fa fa-graduation-cap me-2"></i> My Marksheet
                </Link>
              </div>
            </div>

            <div className="col-12 col-md-4 text-center">
              <div className="position-relative d-inline-block">
                <img
                  src={myProfileImage || logo}
                  alt={loggedUser.name}
                  className="rounded-circle shadow-lg"
                  style={{
                    width: '130px',
                    height: '130px',
                    objectFit: 'cover',
                    border: '4px solid rgba(255, 255, 255, 0.9)',
                    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.3)'
                  }}
                  onError={(e) => {
                    e.target.src = "https://s3.ap-south-1.amazonaws.com/idntica.com/avatar/profileImage/defaultAvatar.jpeg";
                  }}
                />
                <div 
                  className="badge bg-warning text-dark position-absolute bottom-0 start-50 translate-middle-x fw-bold shadow-sm"
                  style={{ borderRadius: '999px', fontSize: '0.75rem', padding: '4px 12px' }}
                >
                  {loggedUser.profile || 'Student'}
                </div>
              </div>
              <div className="mt-3">
                <div className="fw-bold text-white fs-5">{loggedUser.name}</div>
                <div className="text-white-50 small">{loggedUser.email || loggedUser.nickname}</div>
              </div>
            </div>
          </div>
        </div>

        {/* 4 Key Summary Stats */}
        <div className="row g-3 mb-4">
          <div className="col-12 col-sm-6 col-xl-3">
            <div className="card h-100 p-3" style={{ borderLeft: '4px solid #2563eb' }}>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <span className="text-muted small fw-bold text-uppercase">Program Enrolled</span>
                  <h5 className="fw-bold my-1 text-primary">{enrolledCourseName}</h5>
                  <span className="badge success">{enrolledCourseBadge}</span>
                </div>
                <div className="action-btn-circle" style={{ width: '48px', height: '48px', background: 'rgba(37, 99, 235, 0.1)', color: '#2563eb' }}>
                  <i className="fa fa-university fa-lg"></i>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-sm-6 col-xl-3">
            <div className="card h-100 p-3" style={{ borderLeft: '4px solid #059669' }}>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <span className="text-muted small fw-bold text-uppercase">Registration ID</span>
                  <h5 className="fw-bold my-1 text-success">{loggedUser.nickname || '23BTH1001'}</h5>
                  <span className="badge success">Verified & Active</span>
                </div>
                <div className="action-btn-circle" style={{ width: '48px', height: '48px', background: 'rgba(5, 150, 105, 0.1)', color: '#059669' }}>
                  <i className="fa fa-id-card fa-lg"></i>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-sm-6 col-xl-3">
            <div className="card h-100 p-3" style={{ borderLeft: '4px solid #f59e0b' }}>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <span className="text-muted small fw-bold text-uppercase">Course Credits</span>
                  <h5 className="fw-bold my-1 text-warning">{totalCredits} Total Credits</h5>
                  <span className="badge gold">Integral Curriculum</span>
                </div>
                <div className="action-btn-circle" style={{ width: '48px', height: '48px', background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
                  <i className="fa fa-book fa-lg"></i>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-sm-6 col-xl-3">
            <div className="card h-100 p-3" style={{ borderLeft: '4px solid #8b5cf6' }}>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <span className="text-muted small fw-bold text-uppercase">Transcript Status</span>
                  <h5 className="fw-bold my-1" style={{ color: '#8b5cf6' }}>Evaluated</h5>
                  <span className="badge info">Official Grade Card</span>
                </div>
                <div className="action-btn-circle" style={{ width: '48px', height: '48px', background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>
                  <i className="fa fa-certificate fa-lg"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions Grid */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="fw-bold m-0" style={{ color: 'var(--txt-color)' }}>
              <i className="fa fa-th text-primary me-2"></i> Student Quick Actions
            </h4>
            <span className="text-muted small">Instant Navigation</span>
          </div>

          <div className="row g-3">
            <div className="col-12 col-sm-6 col-md-4">
              <Link to="/course" className="text-decoration-none">
                <div className="card h-100 p-4 action-card">
                  <div className="d-flex align-items-center gap-3">
                    <div className="action-icon-box bg-primary-soft">
                      <i className="fa fa-book text-primary fa-2x"></i>
                    </div>
                    <div>
                      <h5 className="fw-bold mb-1">Course Curriculum</h5>
                      <p className="text-muted small mb-0">Browse subject codes, credits & syllabus</p>
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            <div className="col-12 col-sm-6 col-md-4">
              <Link to="/marksheet" className="text-decoration-none">
                <div className="card h-100 p-4 action-card">
                  <div className="d-flex align-items-center gap-3">
                    <div className="action-icon-box bg-success-soft">
                      <i className="fa fa-graduation-cap text-success fa-2x"></i>
                    </div>
                    <div>
                      <h5 className="fw-bold mb-1">Mark Sheet & Transcript</h5>
                      <p className="text-muted small mb-0">View grades & print official transcript</p>
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            <div className="col-12 col-sm-6 col-md-4">
              <Link to="/profile" className="text-decoration-none">
                <div className="card h-100 p-4 action-card">
                  <div className="d-flex align-items-center gap-3">
                    <div className="action-icon-box bg-warning-soft">
                      <i className="fa fa-user text-warning fa-2x"></i>
                    </div>
                    <div>
                      <h5 className="fw-bold mb-1">Student Profile</h5>
                      <p className="text-muted small mb-0">Review personal records & upload photo</p>
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            <div className="col-12 col-sm-6 col-md-4">
              <Link to="/change-password" className="text-decoration-none">
                <div className="card h-100 p-4 action-card">
                  <div className="d-flex align-items-center gap-3">
                    <div className="action-icon-box bg-info-soft">
                      <i className="fa fa-lock text-info fa-2x"></i>
                    </div>
                    <div>
                      <h5 className="fw-bold mb-1">Account Security</h5>
                      <p className="text-muted small mb-0">Change password & credentials</p>
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            {loggedUser.profile === "Super Student" && (
              <div className="col-12 col-sm-6 col-md-4">
                <Link to="/infodesk" className="text-decoration-none">
                  <div className="card h-100 p-4 action-card">
                    <div className="d-flex align-items-center gap-3">
                      <div className="action-icon-box bg-danger-soft">
                        <i className="fa fa-newspaper-o text-danger fa-2x"></i>
                      </div>
                      <div>
                        <h5 className="fw-bold mb-1">News & InfoDesk</h5>
                        <p className="text-muted small mb-0">Manage active campus bulletins</p>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            )}

            <div className="col-12 col-sm-6 col-md-4">
              <Link to="/logout" className="text-decoration-none">
                <div className="card h-100 p-4 action-card">
                  <div className="d-flex align-items-center gap-3">
                    <div className="action-icon-box bg-secondary-soft">
                      <i className="fa fa-sign-out text-danger fa-2x"></i>
                    </div>
                    <div>
                      <h5 className="fw-bold mb-1">Session Logout</h5>
                      <p className="text-muted small mb-0">Safely log out of your student session</p>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* Institution Highlights & Quote */}
        <div className="row g-3">
          <div className="col-12 col-lg-8">
            <div className="card p-4 h-100">
              <div className="d-flex align-items-center gap-3 mb-3">
                <img src={logo} alt="Crest" style={{ width: '48px', height: '48px', borderRadius: '50%' }} />
                <div>
                  <h5 className="fw-bold mb-0">Vidyajyoti College of Theology</h5>
                  <span className="text-muted small">Institute of Religious Studies • Delhi</span>
                </div>
              </div>
              <p className="text-muted" style={{ lineHeight: '1.7' }}>
                Established as an autonomous ecclesiastical faculty, Vidyajyoti prepares women and men for theological reflection, pastoral ministry, and dialogue. The student portal provides unified digital access to examination records, course details, and personal administrative services.
              </p>
              <div className="d-flex flex-wrap gap-3 mt-auto pt-2 border-top">
                <span className="small text-muted"><i className="fa fa-map-marker text-primary me-1"></i> Raj Niwas Marg, Delhi-110054</span>
                <span className="small text-muted"><i className="fa fa-envelope text-primary me-1"></i> vjtheology@gmail.com</span>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-4">
            <div className="card p-4 h-100" style={{ background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(217, 119, 6, 0.15) 100%)', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
              <div className="mb-2 text-warning">
                <i className="fa fa-quote-left fa-2x"></i>
              </div>
              <blockquote className="blockquote mb-3">
                <p className="fs-6 fw-semibold mb-2" style={{ color: 'var(--txt-color)' }}>
                  "Fides Quaerens Intellectum — Faith seeking understanding in the service of justice, reconciliation, and peace."
                </p>
                <footer className="blockquote-footer text-muted small mt-2">
                  Vidyajyoti Theological Faculty
                </footer>
              </blockquote>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}